import { useEvents } from "api/events";
import { memo } from "react";
import {
  CampaignCondition,
  CampaignTriggers,
  CampaignTriggersUnion,
  ReferralCampaignRule,
  SelectedSchemaCondition,
  TransactionItem,
} from "types/campaigns";
import {
  achievementEventReferralCampaignConditions,
  eventReferralCampaignConditions,
  referralCampaignCustomerConditions,
  transactionReferralCampaignConditions,
} from "../rules";
import {
  filterOldTierConditions,
  generateSchemaConditions,
} from "../rulesHelpers";
import { ReferralCampaignRules } from "./ReferralCampaignRules";

interface Props {
  rules: ReferralCampaignRule[];
  trigger: Exclude<CampaignTriggersUnion, "time">;
  event?: string;
  transactionItemsFilters?: TransactionItem[];
  errors: any;
  isEditMode: boolean;
}

const selectConditions = (trigger: CampaignTriggersUnion, event?: string) => {
  switch (trigger) {
    case CampaignTriggers.TRANSACTION:
    case CampaignTriggers.RETURN_TRANSACTION:
      return transactionReferralCampaignConditions;
    case CampaignTriggers.INTERNAL_EVENT:
      return event === "MemberAchievementProgressWasChanged"
        ? achievementEventReferralCampaignConditions
        : eventReferralCampaignConditions;
    case CampaignTriggers.CUSTOM_EVENT:
      return eventReferralCampaignConditions;
    case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
      return eventReferralCampaignConditions;
    case CampaignTriggers.ACHIEVEMENT:
      return referralCampaignCustomerConditions;
    default:
      return [];
  }
};

export const ReferralCampaign = memo(
  ({
    rules,
    trigger,
    event,
    transactionItemsFilters,
    errors,
    isEditMode,
  }: Props) => {
    const triggerConditions = selectConditions(trigger, event);

    const { useCustomEventSchemaDetails } = useEvents();

    const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(
      trigger !== CampaignTriggers.INTERNAL_EVENT ? event : undefined,
    );
    const selectedSchemaConditions: SelectedSchemaCondition[] =
      generateSchemaConditions(selectedSchemaDetails?.schema?.fields);

    // IN OLOY-74333 check why only custom_event trigger injecting schema to conditions
    const conditions = [
      ...triggerConditions,
      ...(trigger === CampaignTriggers.CUSTOM_EVENT
        ? selectedSchemaConditions
        : []),
    ];

    const filteredConditions = filterOldTierConditions({
      conditions,
      isEditMode,
      rules,
    });

    return (
      <ReferralCampaignRules
        conditions={(filteredConditions as CampaignCondition[]) || []}
        selectedSchemaConditions={selectedSchemaConditions}
        errors={errors}
        rules={rules}
        transactionItemsFilters={transactionItemsFilters}
      />
    );
  },
);
